/*
 * Copyright (c) 2021. American Express Company
 */

// third-party
import _ from 'lodash';
import { DateTimeFormatter, LocalDate } from '@js-joda/core';
import Konva from 'konva';
import { Group } from 'konva/lib/Group';
import { Line } from 'konva/lib/shapes/Line';
import { Shape } from 'konva/lib/Shape';
// first-party
import { TLElement } from './TLElement';
import ProjectJaeger from '../ProjectJaeger';
import { JaegerEvent } from '../data/JaegerEvent.enum';
import { CashFlowModel } from 'src/types/cash-flow.model';
import UnitService from '../../../services/unit.service';
import { JaegerService } from '../../../services/jaeger.service';
import CashFlowService from '../../../services/cashflow.service';
import TimelineService from '../services/timeline.service.singleton';
// styles
import colours from 'src/styles/variables.module.scss';
// this does not work as a regular import
const { Locale } = require('@js-joda/locale_en-us');

export default class TLTimeline extends TLElement {
    public parent: ProjectJaeger;
    public jaegerCashFlow: CashFlowModel[];
    public group: Group;
    public dateLine: Line;
    public timelineY: number;
    public timeline: Line;
    public isMonthView: boolean;
    public labelFrequency: number;
    public showMonth: boolean;
    public decadeGroups: Group[] = [];
    public decadeMarkers: Line[] = [];
    public ageTexts: Konva.Text[] = [];
    public yearTexts: Konva.Text[] = [];
    public fiveYearLines: Konva.Line[] = [];
    public dangerBars: Konva.Group[] = [];
    private readonly timelineService: TimelineService;

    constructor(parent: ProjectJaeger) {
        super();
        this.timelineService = TimelineService.getInstance();
        this.parent = parent;
        this.jaegerCashFlow = [];
        this.createGroup();
    }

    private updateParams(): void {
        this.timelineY =
            this.timelineService.getProps().height * this.timelineService.getProps().context.timelinePosition;
        this.isMonthView = this.parent.state.zoom === 'months';
        this.labelFrequency = JaegerService.dateLabelFrequency(
            this.timelineService.getProps().context,
            this.parent.state.zoom,
            this.timelineService.getProps().user
        );
        this.showMonth = this.isMonthView && this.labelFrequency < 12;
    }

    private updateCashFlow(): void {
        this.jaegerCashFlow = this.timelineService.getCashFlow();
    }

    private static hideOutdated(arr: Group[] | Shape[] | Line[] | Konva.Text[]): void {
        arr.forEach((el: Group | Shape | Line | Konva.Text) => {
            if (el.attrs.current) {
                el.show();
            } else {
                el.hide();
            }
        });
    }

    private upsertDecades(): void {
        this.jaegerCashFlow.forEach((cf: CashFlowModel, index: number) => {
            const ageAtCashFlow: number = CashFlowService.getAgeAtCashFlow(this.timelineService.getProps().user, cf);
            const monthNum: number = cf.startDate.monthValue();

            if (
                !ageAtCashFlow ||
                (this.isMonthView && (monthNum - 1) % this.labelFrequency !== 0) ||
                (!this.isMonthView && !Number.isInteger(ageAtCashFlow / this.labelFrequency))
            ) {
                return;
            }

            const xPosition: number = UnitService.getJaegerX(
                this.timelineService.getProps().context,
                this.timelineService.getProps().user,
                cf.startDate
            );

            if (!_.isEmpty(this.decadeGroups[index])) {
                this.decadeGroups[index].x(xPosition);
                this.decadeGroups[index].y(this.timelineY);
                this.decadeGroups[index].attrs.date = cf.startDate;
                this.decadeGroups[index].attrs.current = true;
            } else {
                const decadeGroup: Group = new Group({
                    x: xPosition,
                    y: this.timelineY,
                    perfectDrawEnabled: false,
                    bwType: 'decadeGroup',
                    date: cf.startDate,
                    current: true
                });
                this.decadeGroups[index] = decadeGroup;
                this.group.add(decadeGroup);
            }

            const markerPoints: number[] = [0, 16, 0, -16];
            if (this.decadeMarkers[index]) {
                this.decadeMarkers[index].points(markerPoints);
            } else {
                this.decadeMarkers[index] = new Line({
                    points: markerPoints,
                    stroke: colours.timeline,
                    strokeWidth: 1,
                    lineCap: 'round',
                    lineJoin: 'round',
                    perfectDrawEnabled: false,
                    shadowForStrokeEnabled: false
                });
                this.decadeGroups[index].add(this.decadeMarkers[index]);
            }

            const ageText: string = `Age ${CashFlowService.getAgeAtCashFlow(
                this.timelineService.getProps().user,
                cf
            ).toString()}`;
            if (this.ageTexts[index]) {
                this.ageTexts[index].text(ageText);
            } else {
                this.ageTexts[index] = new Konva.Text({
                    x: this.parent.blockSnapSize * 0.5,
                    y: this.parent.blockSnapSize * -2.2,
                    text: ageText,
                    fontSize: 12,
                    lineHeight: 1.5,
                    fontFamily: "'Helvetica Neue', Helvetica, sans-serif",
                    fill: colours.textDefault,
                    perfectDrawEnabled: false,
                    shadowForStrokeEnabled: false
                });
                this.decadeGroups[index].add(this.ageTexts[index]);
            }

            const yearText: string = cf.startDate.format(
                DateTimeFormatter.ofPattern(this.showMonth ? 'MMM-YYYY' : 'YYYY').withLocale(Locale.US)
            );
            if (this.yearTexts[index]) {
                this.yearTexts[index].text(yearText);
            } else {
                this.yearTexts[index] = new Konva.Text({
                    x: this.parent.blockSnapSize * 0.5,
                    y: this.parent.blockSnapSize * 0.5,
                    text: yearText,
                    fontSize: 11,
                    lineHeight: 1.5,
                    fontFamily: "'Helvetica Neue', Helvetica, sans-serif",
                    fill: colours.textDefault,
                    perfectDrawEnabled: false,
                    shadowForStrokeEnabled: false
                });
                this.decadeGroups[index].add(this.yearTexts[index]);
            }
        });

        TLTimeline.hideOutdated(this.decadeGroups);
    }

    private upsertFiveYearLines(): void {
        // halfway between labels, lining up with age divisible by 5
        const skipHalfway = this.labelFrequency % 2 !== 0;
        const halfFrequency = skipHalfway ? this.labelFrequency : Math.round(this.labelFrequency / 2);
        let startDate = this.jaegerCashFlow[0].startDate;
        const startingAge: number = CashFlowService.getAgeAtCashFlow(
            this.timelineService.getProps().user,
            this.jaegerCashFlow[0]
        );
        if (startingAge % 5) {
            startDate = startDate.plusYears(5 - (startingAge % 5)).minusYears(5);
        }

        let x = UnitService.getJaegerX(
            this.timelineService.getProps().context,
            this.timelineService.getProps().user,
            startDate
        );
        let index: number = 0;
        while (x < this.timeline.width() - this.timelineService.getProps().context.offset) {
            const points: number[] = [x, 0, x, this.timelineService.getProps().height];
            if (this.fiveYearLines[index]) {
                this.fiveYearLines[index].points(points);
                this.fiveYearLines[index].attrs.date = startDate;
                this.fiveYearLines[index].attrs.current = true;
            } else {
                this.fiveYearLines[index] = new Line({
                    points,
                    stroke: colours.fiveYearLine,
                    strokeWidth: 1,
                    perfectDrawEnabled: false,
                    shadowForStrokeEnabled: false,
                    date: startDate,
                    bwType: 'fiveYearLine',
                    current: true
                });
                this.group.add(this.fiveYearLines[index]);
            }

            startDate = this.isMonthView ? startDate.plusMonths(halfFrequency) : startDate.plusYears(halfFrequency);
            x = UnitService.getJaegerX(
                this.timelineService.getProps().context,
                this.timelineService.getProps().user,
                startDate
            );
            index++;
        }

        TLTimeline.hideOutdated(this.fiveYearLines);
    }

    private upsertDateLine(): void {
        const today: LocalDate = LocalDate.now();
        const datePosition: number = UnitService.getJaegerX(
            this.timelineService.getProps().context,
            this.timelineService.getProps().user,
            today
        );
        const points: number[] = [datePosition, 0, datePosition, this.timelineService.getProps().height];

        if (this.dateLine) {
            this.dateLine.points(points);
            this.dateLine.attrs.date = today;
        } else {
            this.dateLine = new Line({
                points,
                stroke: colours.dateline,
                strokeWidth: 1,
                lineCap: 'round',
                lineJoin: 'round',
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
                date: today,
                bwType: 'dateLine'
            });
            this.group.add(this.dateLine);
        }
    }

    private upsertTimeline(): void {
        const points: number[] = [
            0,
            this.timelineY,
            this.parent.stage.width() - this.timelineService.getProps().context.offset,
            this.timelineY
        ];
        if (this.timeline) {
            this.timeline.points(points);
        } else {
            this.timeline = new Line({
                points,
                stroke: colours.timeline,
                strokeWidth: 3,
                lineCap: 'round',
                lineJoin: 'round',
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
                bwType: 'timeline'
            });
            this.group.add(this.timeline);
        }
    }

    private upsertDangerBars(): void {
        // clear old ones
        this.dangerBars.forEach((zone) => zone.remove());

        // stop if we are hiding the dangar bar (step0)
        if (this.timelineService.getProps().user.attributes.hideDangerBar) return;

        // get the cashFlow elements where the dangerZone polarity changes or the type of danger changes
        const cashFlowDeltas = this.jaegerCashFlow.filter((cashFlow: CashFlowModel, index: number) => {
            return (
                index === 0 ||
                cashFlow.dangerZone !== this.jaegerCashFlow[index - 1].dangerZone ||
                cashFlow.danger !== this.jaegerCashFlow[index - 1].danger
            );
        });

        // loop thru the deltas, two at time, the first is the start of a danger zone, the second the end
        let zoneIndex: number = 0;
        for (let i = 0; i < cashFlowDeltas.length; i++) {
            if (cashFlowDeltas[i].danger) {
                const x1: number = UnitService.getJaegerX(
                    this.timelineService.getProps().context,
                    this.timelineService.getProps().user,
                    cashFlowDeltas[i].startDate
                );
                let x2: number;

                if (!cashFlowDeltas[i + 1]) {
                    x2 = UnitService.getJaegerX(
                        this.timelineService.getProps().context,
                        this.timelineService.getProps().user,
                        this.timelineService.getProps().user.rawCashFlow[
                            this.timelineService.getProps().user.rawCashFlow.length - 1
                        ].endDate
                    );
                } else {
                    x2 = UnitService.getJaegerX(
                        this.timelineService.getProps().context,
                        this.timelineService.getProps().user,
                        cashFlowDeltas[i + 1].startDate
                    );
                }

                const y: number = this.timelineY - 16;

                // get existing group
                if (this.dangerBars.length > zoneIndex) {
                    const dangerZone = this.dangerBars[zoneIndex++];
                    // reuse this zone
                    dangerZone.x(x1);
                    dangerZone.y(y);
                    dangerZone.width(x2 - x1);
                    dangerZone.attrs.date1 = cashFlowDeltas[i].startDate;
                    dangerZone.attrs.date2 = cashFlowDeltas[i + 1] && cashFlowDeltas[i + 1].startDate;

                    const rects = dangerZone.getChildren((child) => child.attrs.bwType === 'dangerRect');
                    if (rects.length > 0) {
                        const rect: Konva.Rect = rects[0] as Konva.Rect;
                        rect.width(dangerZone.width());
                        rect.height(dangerZone.height());
                    }
                    dangerZone.on(JaegerEvent.MOUSE_ENTER, () => {
                        this.timelineService.getProps().handleOpenDanger(cashFlowDeltas[i]);
                    });
                    const lines = dangerZone.getChildren((child) => child.attrs.bwType === 'dangerLine');
                    if (lines.length > 0) {
                        const line: Line = lines[0] as Line;
                        line.points([0, 16, dangerZone.width(), 16]);
                        line.stroke(cashFlowDeltas[i].danger === 2 ? colours.bad : colours.dangerWarn);
                    }
                    this.group.add(dangerZone);
                } else {
                    const dangerZone: Group = new Group({
                        class: 'dangerZoneGroup',
                        x: x1,
                        y: this.timelineY - 16,
                        width: x2 - x1,
                        height: 32,
                        perfectDrawEnabled: false,
                        draggable: false,
                        bwType: 'dangerZone',
                        date1: cashFlowDeltas[i].startDate,
                        date2: cashFlowDeltas[i + 1] && cashFlowDeltas[i + 1].startDate
                    });
                    const rect = new Konva.Rect({
                        x: 0,
                        y: 0,
                        width: dangerZone.width(),
                        height: dangerZone.height(),
                        perfectDrawEnabled: false,
                        shadowForStrokeEnabled: false,
                        bwType: 'dangerRect'
                    });
                    dangerZone.add(rect); // without this invisible rect the mouse over is just the height of the
                    // dangerLine
                    dangerZone.on(JaegerEvent.MOUSE_ENTER, () => {
                        this.timelineService.getProps().handleOpenDanger(cashFlowDeltas[i]);
                    });

                    dangerZone.on(JaegerEvent.MOUSE_LEAVE, () => {
                        this.timelineService.getProps().handleCloseDanger();
                    });

                    const dangerLine: Line = new Line({
                        points: [0, 16, dangerZone.width(), 16],
                        stroke: cashFlowDeltas[i].danger === 2 ? colours.bad : colours.dangerWarn,
                        strokeWidth: 3,
                        lineCap: 'round',
                        lineJoin: 'round',
                        perfectDrawEnabled: false,
                        shadowForStrokeEnabled: false,
                        bwType: 'dangerLine'
                    });

                    dangerZone.add(dangerLine);
                    this.dangerBars.push(dangerZone);
                    this.group.add(dangerZone);
                }
            }
        }
    }

    private createGroup(): void {
        if (this.group) {
            this.group.width(this.parent.stage.width());
            this.group.height(this.parent.stage.height());
        } else {
            this.group = new Group({
                id: 'timelineGroup',
                x: 0,
                y: 0,
                width: this.parent.stage.width(),
                height: this.parent.stage.height(),
                perfectDrawEnabled: false
            });
        }
    }

    private resetCurrency(): void {
        this.decadeGroups.forEach((dg) => (dg.attrs.current = false));
        this.fiveYearLines.forEach((dg) => (dg.attrs.current = false));
    }

    public upsert(): void {
        this.resetCurrency();
        this.updateParams();
        this.updateCashFlow();
        this.upsertDateLine();
        this.upsertTimeline();
        this.upsertDecades();
        this.upsertFiveYearLines();
        this.upsertDangerBars();
    }

    public create(): void {
        this.upsert();
        this.parent.mainLayer.add(this.group);
    }

    public destroy(): void {}

    public remove(): void {}

    public updateX(): void {}

    public updateY(): void {}
}
